import * as React from "react";
import { browserLocalStorage } from "@base/browserLocalStorage";

/**
 * Tools in the dev toolbar. The toolbar (and every tool) exists only while dev
 * mode is on: `?devmode=true`, which also allows test cards at checkout.
 *
 * Adding a tool: add it here, then read it with `useDevTool(id)` /
 * `isDevToolOn(id)` where it renders.
 */
export const DEV_TOOLS = [
  {
    id: "tracking",
    label: "tracking",
    title: "Tracker status + live log of what each tracker is sent",
  },
  {
    id: "quizSlides",
    label: "quiz slides",
    title: "Quiz slide picker: jump to any slide",
  },
] as const;

export type DevToolID = (typeof DEV_TOOLS)[number]["id"];

type DevToolsState = {
  tools: Partial<Record<DevToolID, boolean>>;
  /** Toolbar folded down to a small DEV tab (it can cover sticky page buttons). */
  collapsed?: boolean;
  /** Forced risk answer `h` (see useRiskHit); unset = the backend's answer. */
  h?: 0 | 1;
};

// Own key, not the app's browserLocalStorage blob: some quiz views wipe that
// blob on the first slide and only put `devMode` back.
const STORAGE_KEY = "dev_tools";

let state: DevToolsState | undefined;
const listeners = new Set<() => void>();

function read(): DevToolsState {
  if (state) {
    return state;
  }
  try {
    state = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ?? "",
    ) as DevToolsState;
  } catch {
    state = { tools: {} };
  }
  state.tools ??= {};
  return state;
}

function write(next: DevToolsState) {
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage blocked: keep working in memory for this page load
  }
  listeners.forEach((listener) => listener());
}

export function isDevModeOn(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return browserLocalStorage.get().devMode === true;
  } catch {
    return false;
  }
}

export function isDevToolOn(id: DevToolID): boolean {
  return isDevModeOn() && read().tools[id] === true;
}

export function setDevTool(id: DevToolID, on: boolean): void {
  const current = read();
  write({ ...current, tools: { ...current.tools, [id]: on } });
}

export function isDevToolbarCollapsed(): boolean {
  return read().collapsed === true;
}

export function setDevToolbarCollapsed(collapsed: boolean): void {
  write({ ...read(), collapsed });
}

/**
 * The `h` forced from the toolbar, or undefined for the backend's answer.
 * Browser-only: the backend never sees it (unlike its `?rko=` URL param).
 */
export function getForcedH(): 0 | 1 | undefined {
  if (!isDevModeOn()) {
    return undefined;
  }
  const { h } = read();
  return h === 0 || h === 1 ? h : undefined;
}

export function setForcedH(h: 0 | 1 | undefined): void {
  write({ ...read(), h });
}

/** Fires on tool changes and on dev mode turning on/off. */
export function subscribeDevTools(listener: () => void): () => void {
  listeners.add(listener);
  const unsubscribeStorage = browserLocalStorage.subscribe(listener);
  return () => {
    listeners.delete(listener);
    unsubscribeStorage();
  };
}

/** Whether a dev tool is on (always false on the server and outside dev mode). */
export function useDevTool(id: DevToolID): boolean {
  return React.useSyncExternalStore(
    subscribeDevTools,
    () => isDevToolOn(id),
    () => false,
  );
}

export function useDevMode(): boolean {
  return React.useSyncExternalStore(
    subscribeDevTools,
    isDevModeOn,
    () => false,
  );
}

export function useForcedH(): 0 | 1 | undefined {
  return React.useSyncExternalStore(
    subscribeDevTools,
    getForcedH,
    () => undefined,
  );
}

export function useDevToolbarCollapsed(): boolean {
  return React.useSyncExternalStore(
    subscribeDevTools,
    isDevToolbarCollapsed,
    () => false,
  );
}

/** Turn dev mode off (test cards too), fold every tool away and drop a forced `h`. */
export function exitDevMode(): void {
  write({ tools: {} });
  browserLocalStorage.set({ ...browserLocalStorage.get(), devMode: false });
}
